/**
 * Global brand keywords — locale-agnostic.
 *
 * Brands are proper-noun keywords that name a specific company, product,
 * app, or chain and are recognized internationally (or at least cross
 * multiple locales). They live here in ONE place rather than being
 * duplicated per language, because:
 *
 *   • Netflix is "Netflix" in Spain, Brazil, Canada, and Germany.
 *   • Carrefour is "Carrefour" in France, Argentina, and Spain.
 *   • Tim Hortons matches Canadian users regardless of whether they
 *     speak English or French.
 *
 * The locale-aware merge in `../index.ts` combines this map with the
 * common-word keywords from the user's primary language. Brands always
 * fire; common words only fire for users who speak that language.
 *
 * What belongs here:
 *   - Chain / store / company names ("Walmart", "Tesco", "Avianca")
 *   - Brand product names ("Big Mac", "Whopper", "Netflix")
 *   - Apps / platforms ("Uber", "Spotify", "DoorDash")
 *   - Globally-recognized food words ("pizza", "sushi", "ramen")
 *     — the "generous" interpretation chosen during refactor
 *
 * What does NOT belong here:
 *   - Dictionary words in a single language ("supermercado", "lebensmittel",
 *     "natural", "casa", "restaurante")
 *   - Generic category descriptors ("restaurant", "groceries", "phone bill")
 *   - Anything that means different things in different languages
 *
 * When in doubt, see the operational definition in the audit notes.
 */
import type {
  AccommodationSubcategoryKeywords,
  DrinksSubcategoryKeywords,
  EntertainmentSubcategoryKeywords,
  FoodSubcategoryKeywords,
  HealthcareSubcategoryKeywords,
  ShoppingSubcategoryKeywords,
  TransportSubcategoryKeywords,
  UtilitiesSubcategoryKeywords,
} from '../types';

/** Aggregate brand keywords across all subcategory blocks. */
export interface BrandKeywords {
  transportSubcategories: Partial<TransportSubcategoryKeywords>;
  foodSubcategories: Partial<FoodSubcategoryKeywords>;
  drinksSubcategories: Partial<DrinksSubcategoryKeywords>;
  entertainmentSubcategories: Partial<EntertainmentSubcategoryKeywords>;
  shoppingSubcategories: Partial<ShoppingSubcategoryKeywords>;
  accommodationSubcategories: Partial<AccommodationSubcategoryKeywords>;
  healthcareSubcategories: Partial<HealthcareSubcategoryKeywords>;
  utilitiesSubcategories: Partial<UtilitiesSubcategoryKeywords>;
}

/**
 * Shared by the food and healthcare blocks on purpose: the taxonomy lists
 * `supplements` under both, and the cloud tier breaks the tie.
 */
// Sports nutrition → food/supplements
const sportsNutritionBrands = [
  // Global
  'myprotein',
  'optimum nutrition',
  'bulk powders',
  // Germany / Austria
  'esn',
  'foodspring',
  'more nutrition',
  // France
  'nutrimuscle',
  'eafit',
  // Brazil
  'growth supplements',
  'integralmedica',
  'integralmédica',
  'max titanium',
  'dux nutrition',
  'black skull',
  // Iberia
  'prozis',
  'hsn',
  'nutrytec',
  'nutrisport',
];

// Vitamins, collagen, health stores → healthcare/supplements
const healthSupplementsBrands = [
  // Global
  'iherb',
  'gnc',
  'holland & barrett',
  'holland and barrett',
  'herbalife',
  'centrum',
  'solgar',
  "nature's bounty",
  'natures bounty',
  'now foods',
  'garden of life',
  'vital proteins',
  // Germany / Austria
  'doppelherz',
  'orthomol',
  'abtei',
  'sunday natural',
  // France
  'nutri&co',
  'juvamine',
  'forté pharma',
  'forte pharma',
  // Brazil
  'probiótica',
  'probiotica',
  'mundo verde',
  // LatAm
  'omnilife',
  'fuxion',
];

/**
 * The empty scaffold. Brands will be migrated here from per-language
 * files in subsequent commits — one category at a time, with manual
 * review of each candidate keyword.
 *
 * During migration the locale-aware merge falls back to the legacy
 * "merge all languages" behavior whenever a category is still empty
 * here, so this can be filled incrementally without breaking anything.
 */
export const brandKeywords: BrandKeywords = {
  transportSubcategories: {
    bus: [
      // North America intercity
      'greyhound',
      'megabus',
      // UK
      'national express',
      // Europe / global
      'flixbus',
      'ouibus',
      'postbus',
      // Spain
      'alsa',
      'avanza',
      // Latin America / Colombia transit
      'transmilenio',
      'sitp',
      'mio cali',
      // Canada transit
      'stm bus',
      'sto',
      'stl',
      'rtc',
      'oc transpo',
      'translink bus',
      'ttc bus',
      'presto bus',
      'go bus',
      'york region transit',
      'brampton transit',
      'miway',
    ],
    train: [
      // US / Canada
      'amtrak',
      'via rail',
      'go train',
      'caltrain',
      'mta train',
      'lirr',
      'metro-north',
      'nj transit',
      'septa',
      'exo',
      // UK / Europe
      'eurostar',
      'thameslink',
      'great western',
      'thalys',
      // France
      'tgv',
      'ter',
      'sncf',
      'intercités',
      'ouigo',
      // Spain
      'renfe',
      'ave',
      'iryo',
      'avlo',
      // Germany / Austria
      'db',
      'deutsche bahn',
      'ice',
      'intercity',
      's-bahn',
      // Brazil
      'cptm',
      'supervia',
    ],
    flight: [
      // US
      'delta',
      'united',
      'american airlines',
      'southwest',
      'jetblue',
      'spirit',
      'alaska airlines',
      // Canada
      'air canada',
      'westjet',
      'porter airlines',
      'flair airlines',
      'flair air',
      'lynx air',
      'air transat',
      'sunwing',
      // UK / Ireland
      'british airways',
      'virgin atlantic',
      'jet2',
      'aer lingus',
      // Europe budget
      'easyjet',
      'ryanair',
      // Oceania
      'qantas',
      'jetstar',
      'virgin australia',
      'air new zealand',
      // Spain
      'iberia',
      'vueling',
      'air europa',
      'plus ultra',
      'binter',
      // Latin America
      'aeroméxico',
      'aeromexico',
      'latam',
      'avianca',
      'copa airlines',
      'viva aerobus',
      'volaris',
      'easyfly',
      'wingo',
      'satena',
      'aerolíneas argentinas',
      'flybondi',
      'jetsmart',
      'sky airline',
      // France
      'air france',
      'transavia',
      'klm',
      'corsair',
      'air caraïbes',
      'french bee',
      // Belgium / Portugal
      'brussels airlines',
      'tap portugal',
      'tap air portugal',
      // Brazil
      'gol',
      'azul',
      'tam',
      'sata',
      // DACH
      'lufthansa',
      'eurowings',
      'condor',
      'tuifly',
      'austrian airlines',
      'swiss',
    ],
    bike: [
      // Bike / scooter share apps
      'lime scooter',
      'bird scooter',
      'vélib',
      'nextbike',
      // Brazilian micromobility
      'itaú bike',
    ],
    rideshare: [
      // Global apps
      'uber',
      'lyft',
      'didi',
      'ola',
      'grab',
      'bolt',
      // Spain / LatAm
      'cabify',
      'beat',
      '99',
      'indriver',
      'in driver',
      // Europe
      'blablacar',
      'free now',
      'heetch',
      'kapten',
      // Canada
      'communauto',
    ],
    metro: [
      // Major systems
      'bart',
      'wmata',
      'mta subway',
      'london underground',
      'sydney metro',
      'mrt',
      // Canada
      'ttc',
      'presto',
      'presto card',
      'stm metro',
      'rem montreal',
      'translink',
      // City metros (Spanish-speaking)
      'metro de madrid',
      'metro bogotá',
      'metro de medellín',
      'metro df',
      'metro santiago',
      'metro lima',
    ],
    fuel: [
      // Global majors
      'shell',
      'exxon',
      'chevron',
      'bp',
      'mobil',
      'arco',
      'esso',
      'texaco',
      'caltex',
      // North America
      'petro-canada',
      'petro canada',
      'husky',
      'ultramar',
      'couche-tard',
      'circle k',
      '7-eleven fuel',
      // Oceania
      'z energy',
      // Europe
      'cepsa',
      'galp',
      'repsol',
      'total',
      'totalenergies',
      'avia',
      'aral',
      'prio',
      // Latin America
      'pemex',
      'axion',
      'copec',
      'petrobras',
      'terpel',
      'esso colombia',
      'ipiranga',
      'ale',
      'br mania',
      'ypf',
    ],
    toll: [
      // Brazilian toll tags (brand-name systems)
      'sem parar',
      'conectcar',
      'veloe',
    ],
    rental: [
      // Global majors
      'hertz',
      'avis',
      'enterprise rent',
      'enterprise rent-a-car',
      'budget car rental',
      'budget rent',
      'zipcar',
      'alamo rent',
      'sixt',
      'getaround',
      'turo',
      'national car rental',
      'europcar',
      'goldcar',
      // Brazil
      'localiza',
      'movida',
      'unidas',
      // Canada
      'communauto',
      'discount auto',
    ],
    maintenance: ['jiffy lube'],
    airport: [
      'tsa',
      'tsa precheck',
      // Brazilian airports
      'guarulhos',
      'galeão',
      'congonhas',
      'viracopos',
      'brasília aeroporto',
    ],
  },
  foodSubcategories: {
    pizza: [
      'pizza',
      'pizzeria',
      'pizza hut',
      'dominos',
      'papa johns',
      'little caesars',
      'pizza margarita',
      'margarita pizza',
      'margherita',
    ],
    sushi: ['sushi', 'sashimi', 'tempura', 'temaki', 'maki', 'nigiri', 'poke'],
    hamburger: ['burger', 'hamburger', 'cheeseburger', 'whopper', 'big mac'],
    sandwich: ['sandwich', 'panini', 'wrap', 'hoagie'],
    soup: [
      'ramen',
      'pho',
      'gumbo',
      'minestrone',
      'bisque',
      'chowder',
      'consommé',
    ],
    steak: [
      'steak',
      'ribeye',
      'sirloin',
      't-bone',
      'filet mignon',
      'prime rib',
      'steakhouse',
      'outback steakhouse',
      'longhorn steakhouse',
    ],
    salad: ['sweetgreen', 'chopt'],
    pastry: [
      'croissant',
      'danish',
      'muffin',
      'scone',
      'donut',
      'eclair',
      'macaron',
      'cinnamon roll',
      'cupcake',
      'brioche',
      'pain au chocolat',
      'mille-feuille',
      'paris-brest',
    ],
    dessert: [
      'tiramisu',
      'cheesecake',
      'flan',
      'mousse',
      'brownie',
      'crème brûlée',
    ],
    candy: [
      'snickers',
      'twix',
      'kit kat',
      'hershey',
      'm&ms',
      'haribo',
      'milka',
      'mars',
    ],
    icecream: [
      'gelato',
      'sorbet',
      'sundae',
      'baskin robbins',
      'dairy queen',
      'cold stone',
      'ben and jerrys',
      "ben and jerry's",
      'haagen-dazs',
      'häagen-dazs',
      'kibon',
      'mövenpick',
    ],
    snacks: ['doritos', 'lays', 'pringles', 'cheetos'],
    fruit: [
      'apple',
      'banana',
      'berries',
      'mango',
      'pineapple',
      'strawberry',
      'avocado',
    ],
    meat: [
      'beef',
      'pork',
      'lamb',
      'chicken',
      'turkey',
      'bacon',
      'sausage',
      'ham',
    ],
    seafood: [
      'salmon',
      'tuna',
      'shrimp',
      'lobster',
      'crab',
      'oyster',
      'scallop',
      'prawn',
    ],
    dairy: ['milk', 'cheese', 'yogurt', 'butter', 'cream', 'mozzarella'],
    breakfast: ['brunch', 'pancakes', 'waffles', 'müesli', 'muesli'],
    restaurant: [
      // Global chains
      'mcdonalds',
      'burger king',
      'wendys',
      'kfc',
      'taco bell',
      'chick-fil-a',
      'popeyes',
      'subway',
      'olive garden',
      'cheesecake factory',
      'applebees',
      'red lobster',
      'outback',
      'dennys',
      'ihop',
      'cracker barrel',
      'nandos',
      'wagamama',
      // Canada
      'swiss chalet',
      'boston pizza',
      'st-hubert',
      'a&w',
      'harveys',
      'la cage aux sports',
      'mikes',
      'le commensal',
      // Brazil
      'mc',
      'bk',
      'bobs',
      "habib's",
      'giraffas',
      'spoleto',
      // Mexico / LatAm
      'vips',
      'sanborns',
      // Colombia
      'frisby',
      'el corral',
      'crepes y waffles',
      'andrés carne de res',
      'wok',
      'archies',
      'sandwich qbano',
      'la brasa roja',
      // Spain / Europe chains
      'pollo campero',
      'quick',
      'nordsee',
      'cien montaditos',
      // Germany / DACH
      'hans im glück',
    ],
    bakery: ['panera bread', 'panera'],
    cafe: ['pret a manger'],
    delivery: [
      'uber eats',
      'doordash',
      'grubhub',
      'postmates',
      'seamless',
      'skip the dishes',
      'skipthedishes',
      'foodora',
      'instacart',
      'deliveroo',
      'just eat',
      'menulog',
      'goodfood',
      // LatAm
      'rappi',
      'pedidos ya',
      'didi food',
      'cornershop',
      'glovo',
      'merqueo',
      'mercadoni',
      // Brazil
      'ifood',
      '99 food',
      'aiqfome',
      'zé delivery',
      'tele-entrega',
      // Germany
      'lieferando',
      'lieferheld',
    ],
    groceries: [
      // US
      'walmart',
      'target',
      'kroger',
      'safeway',
      'whole foods',
      'trader joes',
      'costco',
      'aldi',
      'lidl',
      // Canada
      'loblaws',
      'sobeys',
      'no frills',
      'real canadian superstore',
      'maxi',
      'provigo',
      'iga',
      'save-on-foods',
      'longos',
      'fortinos',
      'farm boy',
      'super c',
      'adonis',
      // UK
      'tesco',
      'sainsburys',
      'asda',
      'morrisons',
      'waitrose',
      'marks and spencer',
      'm&s food',
      // Australia / NZ
      'coles',
      'woolworths',
      'new world',
      'pak n save',
      'countdown',
      // Spain
      'mercadona',
      'carrefour',
      'dia',
      'alcampo',
      'consum',
      'eroski',
      'hipercor',
      // Colombia
      'éxito',
      'exito',
      'carulla',
      'olímpica',
      'olimpica',
      'jumbo',
      'd1',
      'tiendas d1',
      'justo y bueno',
      'ara',
      'mercadefam',
      'la 14',
      // Mexico
      'cencosud',
      'oxxo',
      'soriana',
      'chedraui',
      'sams club',
      'bodega aurrera',
      // Argentina
      'coto',
      'disco',
      'vea',
      // Chile
      'líder',
      'lider',
      'unimarc',
      'santa isabel',
      // Peru
      'wong',
      'tottus',
      'plaza vea',
      'metro peru',
      'mass',
      'vivanda',
      // Uruguay
      'tienda inglesa',
      'devoto',
      // Brazil
      'pão de açúcar',
      'extra',
      'atacadão',
      'assaí',
      'makro',
      'hortifruti',
      'sacolão',
      'quitanda',
      // Portugal
      'continente',
      'pingo doce',
      'auchan',
      'mini preço',
      'minipreço',
      'intermarché',
      'el corte inglés pt',
      // France
      'leclerc',
      'monoprix',
      'casino',
      'super u',
      'hyper u',
      'picard',
      'naturalia',
      'biocoop',
      // Germany / Austria / Switzerland
      'rewe',
      'edeka',
      'kaufland',
      'penny',
      'netto',
      'real',
      'hofer',
      'spar',
      'billa',
      // Food only, not shopping: the cloud tier doesn't know this
      // Austrian grocer and calls it a restaurant, so the keyword tier
      // must answer alone (a food/shopping tie would defer to the cloud).
      'foodpoint',
      'merkur',
      'denns biomarkt',
      'alnatura',
      'migros',
      'coop',
      'denner',
      'manor food',
      'volg',
      'globus',
    ],
    supplements: sportsNutritionBrands,
  },
  drinksSubcategories: {
    water: [
      'evian',
      'perrier',
      'san pellegrino',
      's.pellegrino',
      'volvic',
      'vittel',
      'fiji water',
    ],
    juice: ['smoothie', 'smoothies'],
    soda: [
      'coca-cola',
      'coca cola',
      'pepsi',
      'sprite',
      'fanta',
      '7up',
      'dr pepper',
      'schweppes',
    ],
    coffee: [
      'espresso',
      'latte',
      'cappuccino',
      'macchiato',
      'frappuccino',
      'nespresso',
      'starbucks',
      'dunkin',
      'costa coffee',
      'tim hortons',
      'second cup',
      'café van houtte',
      'caffè nero',
      'columbus café',
      // LatAm coffee chains
      'juan valdez',
      'tostao',
      // Bare 'oma' is German for grandma.
      'café oma',
      'café quindío',
      'mcafé',
      'mccafé',
      'cielito querido',
    ],
    tea: ['chai', 'chai latte', 'matcha', 'bubble tea', 'boba'],
    energy: ['red bull', 'monster energy', 'gatorade', 'powerade'],
    beer: [
      'beer',
      'heineken',
      'corona extra',
      'coronita',
      'guinness',
      'stella artois',
      'budweiser',
      'bud light',
      'modelo especial',
      'carlsberg',
      'kronenbourg',
      'paulaner',
      'estrella damm',
      'mahou',
      'cerveza quilmes',
      'club colombia',
      'skol',
      'brahma',
      'cerveja sagres',
      'super bock',
    ],
    wine: ['wine', 'champagne', 'prosecco', 'cava', 'rosé'],
    cocktails: [
      // 'cocktail' is the universal term (English loanword in ES/FR/PT/DE too)
      'cocktail',
      'cocktails',
      'mojito',
      'margarita cocktail',
      'margaritas',
      'daiquiri',
      'manhattan cocktail',
      'negroni',
      'gin tonic',
      'gin and tonic',
      'spritz',
      'aperol',
      'caipirinha',
      'pina colada',
      'piña colada',
      'cosmopolitan cocktail',
      'old fashioned',
      'martini',
      'kir royal',
      'sangria',
      'sangría',
    ],
    spirits: [
      'whisky',
      'whiskey',
      'vodka',
      'gin',
      'rum',
      'tequila',
      'bourbon',
      'scotch',
      'cognac',
      'mezcal',
      'pisco',
    ],
  },
  entertainmentSubcategories: {
    music: [
      'spotify',
      'apple music',
      'tidal',
      'deezer',
      'youtube music',
      'soundcloud',
      'pandora',
      'amazon music',
    ],
    streaming: [
      // Global / US
      'netflix',
      'hulu',
      'disney+',
      'disney plus',
      'hbo',
      'hbo max',
      'max',
      'amazon prime',
      'amazon prime video',
      'prime video',
      'apple tv',
      'apple tv+',
      'youtube tv',
      'youtube premium',
      'paramount',
      'paramount+',
      'peacock',
      'discovery+',
      'streaming',
      // Canada
      'crave',
      'club illico',
      'tou.tv',
      // UK
      'bbc iplayer',
      'itvx',
      'now tv',
      // Oceania
      'stan',
      'binge',
      'kayo',
      // Latin America / Brazil
      'globoplay',
      'star+',
      'star plus',
      // France
      'canal+',
      'ocs',
      'salto',
      'molotov',
      // DACH
      'dazn',
      'rtl+',
      'sky',
      'sky go',
      'joyn',
      'magenta tv',
      'wow',
      'ard mediathek',
      'zdf mediathek',
      'ard plus',
      'servus tv',
      'orf on',
      'play suisse',
      // Portugal
      'opto',
      'rtp play',
      'sapo tv',
      'meo go',
      'nos play',
    ],
    gaming: [
      'playstation',
      'ps4',
      'ps5',
      'xbox',
      'nintendo',
      'switch',
      'steam',
      'epic games',
      'esports',
      'gaming',
      'console',
      'arcade',
    ],
    books: [
      // Audiobook concept in every language — moved here so cross-language
      // merge doesn't drift it back into the podcast subcategory by accident.
      'kindle',
      'audible',
      'audiobook',
      'audiolibro',
      'audiolivro',
      'livre audio',
      'hörbuch',
      // Bookstore chains
      'saraiva',
      'thalia',
      'hugendubel',
      'amazon books',
      'barnes noble',
      'ebook',
    ],
    fitness: [
      // Universal loanwords (used unchanged in every language)
      'yoga',
      'pilates',
      'crossfit',
      // Gym chains
      'smart fit',
      'bio ritmo',
      'mcfit',
      'fitness first',
      'urban sports club',
    ],
    events: [
      // Famous festivals / annual events (proper-noun brands)
      'rock in rio',
      'oktoberfest',
      'broadway',
    ],
    sports: [
      // Major leagues
      'bundesliga',
    ],
    podcasts: ['joe rogan', 'npr', 'cbc radio', 'podpah'],
    outdoor: [
      // Brazilian theme parks (brand-name attractions)
      'hopi hari',
      'beto carrero',
    ],
    water: [
      // Universal water-sport loanwords
      'snorkel',
      'snorkeling',
      'surf',
      'surfing',
      'kayak',
      'kayaking',
      'parasail',
      'parasailing',
      'paddleboard',
      'stand up paddle',
      'sup',
      'jet ski',
      'windsurf',
      'kitesurf',
      'wakeboard',
      'rafting',
    ],
    movies: [
      // Cinema chains
      'imax',
      'amc theaters',
      'regal cinemas',
      'cinemark',
      'cinépolis',
      'cinepolis',
      'cineplex',
      'uci',
      'kinoplex',
      'cinemaxx',
      'cinestar',
      'filmpalast',
    ],
  },
  shoppingSubcategories: {
    clothes: [
      'zara',
      'h&m',
      'uniqlo',
      'gap',
      'c&a',
      'peek & cloppenburg',
      'renner',
      'riachuelo',
      'marisa',
    ],
    electronics: [
      'best buy',
      'apple store',
      'samsung',
      'saturn',
      'media markt',
      'conrad',
      'cyberport',
      'fast shop',
      'kalunga',
      'magazineluiza',
      'magazine luiza',
      'casas bahia',
    ],
    groceries: [
      // Mirrors brandKeywords.foodSubcategories.groceries — the same
      // chains belong in both the food-shopping (groceries the activity)
      // and the shopping (the venue / spending category) routes. The
      // cross-detector groceries dualism is intentional.
      'walmart',
      'target',
      'kroger',
      'safeway',
      'whole foods',
      'trader joes',
      'costco',
      'aldi',
      'lidl',
      'loblaws',
      'sobeys',
      'no frills',
      'real canadian superstore',
      'maxi',
      'provigo',
      'iga',
      'save-on-foods',
      'longos',
      'fortinos',
      'farm boy',
      'super c',
      'adonis',
      'tesco',
      'sainsburys',
      'asda',
      'morrisons',
      'waitrose',
      'marks and spencer',
      'm&s food',
      'coles',
      'woolworths',
      'new world',
      'pak n save',
      'countdown',
      'mercadona',
      'carrefour',
      'dia',
      'alcampo',
      'consum',
      'eroski',
      'hipercor',
      'éxito',
      'exito',
      'carulla',
      'olímpica',
      'olimpica',
      'jumbo',
      'd1',
      'tiendas d1',
      'justo y bueno',
      'ara',
      'mercadefam',
      'la 14',
      'cencosud',
      'oxxo',
      'soriana',
      'chedraui',
      'sams club',
      'bodega aurrera',
      'coto',
      'disco',
      'vea',
      'líder',
      'lider',
      'unimarc',
      'santa isabel',
      'wong',
      'tottus',
      'plaza vea',
      'metro peru',
      'mass',
      'vivanda',
      'tienda inglesa',
      'devoto',
      'pão de açúcar',
      'extra',
      'atacadão',
      'assaí',
      'makro',
      'hortifruti',
      'sacolão',
      'quitanda',
      'continente',
      'pingo doce',
      'auchan',
      'mini preço',
      'minipreço',
      'intermarché',
      'el corte inglés pt',
      'leclerc',
      'monoprix',
      'casino',
      'super u',
      'hyper u',
      'picard',
      'naturalia',
      'biocoop',
      'rewe',
      'edeka',
      'kaufland',
      'penny',
      'netto',
      'real',
      'hofer',
      'spar',
      'billa',
      'merkur',
      'denns biomarkt',
      'alnatura',
      'migros',
      'coop',
      'denner',
      'manor food',
      'volg',
      'globus',
    ],
    online: [
      'amazon',
      'ebay',
      'aliexpress',
      'etsy',
      'shopify',
      'wayfair',
      'shein',
      'temu',
      'asos',
      'argos',
      'kogan',
      'the iconic',
      // LatAm
      'mercadolibre',
      'mercado libre',
      'americanas',
      'submarino',
      'magalu',
      'magazine luiza',
      'kabum',
      // France
      'fnac',
      'cdiscount',
      'vinted',
      'le bon coin',
      'leboncoin',
      // Brazil / Portugal
      'shopee',
      // Germany / Austria
      'otto',
      'zalando',
      'about you',
    ],
    furniture: [
      'ikea',
      'ashley furniture',
      'west elm',
      'crate and barrel',
      'pottery barn',
      'tok&stok',
      'etna',
      'poco',
      'höffner',
      'möbel kraft',
      'roller',
      'xxxlutz',
    ],
    shoes: ['nike', 'adidas', 'puma', 'havaianas', 'arezzo'],
    books: [
      // Most "book" brand keywords (Kindle/Audible/Saraiva/Thalia/...)
      // live under entertainmentSubcategories.books. Just adding the
      // dedicated bookstore retailers that also surface in shopping.
      'barnes noble',
      'cultura',
    ],
    toys: [
      'lego',
      'ri happy',
      'pbkids',
      'playmobil',
      'toys r us',
      'spiele max',
    ],
    department: [
      // US
      'macys',
      'nordstrom',
      'sears',
      'jcpenney',
      'kohls',
      'bloomingdales',
      // Canada
      'hudsons bay',
      'the bay',
      'la baie',
      'la baie d hudson',
      'winners',
      'marshalls',
      'tj maxx',
      'simons',
      // UK / Ireland
      'john lewis',
      'selfridges',
      'debenhams',
      'harrods',
      'fortnum',
      // Oceania
      'david jones',
      'myer',
      'farmers',
      // Mexico / LatAm
      'liverpool',
      'palacio de hierro',
      'el corte inglés',
      'falabella',
      'ripley',
      'pepe ganga',
      'la 14',
      'paris',
      'cencosud',
      'suburbia',
      'coppel',
      'sanborns',
      // France
      'galeries lafayette',
      'printemps',
      'le bhv',
      // Brazil
      'renner',
      'c&a',
      'riachuelo',
      'marisa',
      'pernambucanas',
      // DACH
      'galeria kaufhof',
      'karstadt',
      'breuninger',
      'peek cloppenburg',
    ],
    jewelry: ['vivara', 'pandora'],
    cosmetics: [
      'sephora',
      'ulta',
      'marionnaud',
      'douglas',
      'dm',
      'rossmann',
      'o boticário',
      'natura',
      'avon',
    ],
    sports: [
      'decathlon',
      'intersport',
      'dicks sporting',
      'sports authority',
      'modells',
      'sportchek',
      'sports check',
      'sport scheck',
      'centauro',
      'netshoes',
    ],
    hardware: [
      // US / Canada
      'home depot',
      'lowes',
      'canadian tire',
      'home hardware',
      'rona',
      'reno depot',
      'réno-dépôt',
      'menards',
      'ace hardware',
      'true value',
      'bmr',
      'canac',
      'patrick morin',
      // UK
      'b&q',
      'wickes',
      'screwfix',
      'homebase',
      // Oceania
      'bunnings',
      'mitre 10',
      // Spain / France
      'leroy merlin',
      'castorama',
      'brico dépôt',
      'brico depot',
      'mr bricolage',
      // LatAm
      'sodimac',
      'easy',
      'homecenter',
      'constructor',
      'maestro',
      'promart',
      'bauhaus',
      'construmart',
      // Portugal
      'aki',
      'bricomarché',
      'maxmat',
      'bricodepot',
      // Germany / Austria / Switzerland
      'obi',
      'hornbach',
      'hagebau',
      'baumax',
      'lagerhaus',
      'jumbo baumarkt',
      'landi',
      'bauland',
    ],
    garden: ['garden center'],
  },
  accommodationSubcategories: {
    hotel: [
      // Global / US chains
      'marriott',
      'hilton',
      'hyatt',
      'sheraton',
      'westin',
      'four seasons',
      'holiday inn',
      'best western',
      'wyndham',
      // Canada
      'fairmont',
      'delta hotels',
      'sandman',
      'hôtel le germain',
      'auberge saint-antoine',
      'château frontenac',
      // UK
      'premier inn',
      'travelodge',
      // Europe / France
      'ibis',
      'novotel',
      'mercure',
      'accor',
      'b&b hôtels',
      'b&b hotels',
      'campanile',
      'kyriad',
      // Spain / LatAm
      'meliá',
      'melia',
      'nh hoteles',
      'iberostar',
      'barceló',
      'barcelo',
      'riu',
      'paradores',
      'ghl hoteles',
      'estelar',
      'decameron',
      // DACH
      'maritim',
      'steigenberger',
      'kempinski',
      'mövenpick hotel',
      'falkensteiner',
      'arcotel',
      'austria trend',
      'meininger',
      // Booking platforms (live in hotel since most expenses booked through them)
      'booking',
      'booking.com',
      'expedia',
      'hotels.com',
      'agoda',
      'trivago',
    ],
    airbnb: [
      'airbnb',
      'vrbo',
      'fewo-direkt',
      'fewo direkt',
      'traum-ferienwohnungen',
    ],
  },
  healthcareSubcategories: {
    pharmacy: [
      // US
      'cvs',
      'walgreens',
      'rite aid',
      // Canada
      'shoppers drug mart',
      'rexall',
      'pharmaprix',
      'london drugs',
      'jean coutu',
      'uniprix',
      'familiprix',
      'brunet',
      'proxim',
      // UK / Ireland
      'boots pharmacy',
      'boots chemist',
      'lloydspharmacy',
      // Oceania
      'chemist warehouse',
      'priceline pharmacy',
      // Latin America
      'cruz verde',
      'fybeca',
      'dr simi',
      'farmacias del ahorro',
      'farmacia san pablo',
      'cafam',
      'colsubsidio',
      'drogas la rebaja',
      'la locura',
      'olímpica droguería',
      'pasteur',
      'farmatodo',
      'inkafarma',
      'mifarma',
      'farmacity',
      'salcobrand',
      'ahumada',
      'farmacias guadalajara',
      'benavides',
      // Brazil
      'droga raia',
      'drogasil',
      'pacheco',
      // Portugal
      'wells',
      'farmácia portuguesa',
      'continente saúde',
      'farmácia sá da bandeira',
      // France
      'pharmacie lafayette',
      // DACH
      'doc morris',
      'shop apotheke',
      'bipa',
      'bahnhof apotheke',
      'sun store',
      'toppharm',
      'amavita',
    ],
    hospital: [
      // Brazilian lab chains
      'lavoisier',
      'fleury',
      // US
      'samu',
    ],
    insurance: [
      // France
      'ameli',
      'mgen',
      'harmonie mutuelle',
      'axa santé',
      // Brazil
      'unimed',
      'sul américa',
      'bradesco saúde',
      'amil',
      // Germany
      'aok',
      'barmer',
      'tk',
    ],
    fitness: [
      // Universal loanwords used unchanged in every language
      'yoga',
      'pilates',
      'crossfit',
      // Gym / wellness chains
      'smart fit',
      'bio ritmo',
      'mcfit',
      'fitness first',
      'urban sports club',
    ],
    supplements: healthSupplementsBrands,
  },
  utilitiesSubcategories: {
    electricity: [
      // Canada
      'hydro-québec',
      'hydro quebec',
      'bc hydro',
      'toronto hydro',
      'enmax',
      'epcor',
      'manitoba hydro',
      'saskpower',
      'nb power',
      // UK
      'octopus energy',
      'british gas electricity',
      'edf energy',
      'eon next',
      // Oceania
      'agl',
      'origin energy',
      'energy australia',
      // Spain / LatAm
      'cfe',
      'edenor',
      'edesur',
      'naturgy',
      'iberdrola',
      'endesa',
      'epm',
      'codensa',
      'enel',
      'air-e',
      'afinia',
      'cge',
      'saesa',
      'edemsa',
      'edesa',
      'edelnor',
      'luz del sur',
      // Brazil / Portugal
      'cpfl',
      'eletropaulo',
      'light',
      'cemig',
      'edp',
      'galp power',
      'iberdrola portugal',
      'endesa portugal',
      // France
      'edf',
      'engie',
      'totalenergies',
      'enercoop',
      'ekwateur',
      'planète oui',
      'mint énergie',
      // DACH
      'eon',
      'rwe',
      'vattenfall',
      'stadtwerke',
      'enbw',
      'ewe',
      'lichtblick',
    ],
    water: [
      // Brazil
      'sabesp',
      'compesa',
      'caesb',
      'copasa',
      'embasa',
    ],
    gas: [
      // Brazil
      'comgás',
      'ceg',
      'ultragaz',
      'liquigás',
    ],
    internet: [
      // US
      'comcast',
      'verizon',
      'at&t',
      'spectrum',
      'cox',
      'xfinity',
      // Canada
      'rogers',
      'bell',
      'telus',
      'vidéotron',
      'videotron',
      'cogeco',
      'shaw',
      'eastlink',
      'fizz',
      'ebox',
      'oxio',
      // UK
      'sky broadband',
      'virgin media',
      'talktalk',
      'plusnet',
      'bt internet',
      // Oceania
      'nbn',
      // Spain / LatAm
      'telmex',
      'izzi',
      'totalplay',
      'movistar',
      'vodafone',
      'claro',
      'tigo',
      'etb',
      'jazztel',
      'masmovil',
      'pepephone',
      'digi',
      'lowi',
      'cablevisión',
      'fibertel',
      'arnet',
      'vtr',
      'gtd',
      // Brazil / Portugal
      'vivo',
      'oi',
      'tim',
      'meo',
      'nos',
      'vodafone pt',
      'nowo',
      // France
      'orange',
      'free',
      'sfr',
      'bouygues',
      'bouygues telecom',
      'red sfr',
      'sosh',
      // DACH
      'telekom',
      '1&1',
      'unitymedia',
      'kabel deutschland',
      'magenta',
      'a1',
      'drei',
      'swisscom',
      'sunrise',
      'salt',
      'pyur',
      'congstar',
    ],
    phone: [
      // Canada
      'fido',
      'koodo',
      'virgin plus',
      'freedom mobile',
      'public mobile',
      'chatr',
      // UK
      'ee mobile',
      'three',
      'giffgaff',
      'lebara',
      // Oceania
      'telstra',
      'optus',
      'tpg',
      'spark nz',
      '2degrees',
      // Spain / LatAm
      'personal',
      'tigo móvil',
      'etb móvil',
      'wom',
      'entel',
      'bitel',
      'yoigo',
      'pepephone móvil',
      // Brazil
      'nextel',
      'wtf',
      'uzo',
      // France
      'lyca',
      'lycamobile',
      'la poste mobile',
      'prixtel',
      'free mobile',
      // DACH
      'aldi talk',
      'lidl connect',
      'magenta mobil',
      'a1 mobil',
      'drei mobil',
      'yallo',
      'wingo',
      'meo móvel',
      'nos móvel',
      'vodafone móvel',
    ],
    maintenance: [
      // Brazilian property tax acronym
      'iptu',
    ],
    streaming: [
      // Mirrors entertainmentSubcategories.streaming — the same brands
      // belong here because the utility/streaming and entertainment/
      // streaming dualism is intentional (Netflix as a TV bill vs Netflix
      // as entertainment is mostly intent-driven).
      'netflix',
      'hulu',
      'disney+',
      'disney plus',
      'hbo',
      'hbo max',
      'max',
      'amazon prime',
      'amazon prime video',
      'prime video',
      'apple tv',
      'apple tv+',
      'youtube tv',
      'youtube premium',
      'paramount',
      'paramount+',
      'peacock',
      'discovery+',
      'crave',
      'club illico',
      'tou.tv',
      'bbc iplayer',
      'itvx',
      'now tv',
      'stan',
      'binge',
      'kayo',
      'globoplay',
      'star+',
      'star plus',
      'canal+',
      'ocs',
      'salto',
      'molotov',
      'dazn',
      'rtl+',
      'sky',
      'sky go',
      'joyn',
      'magenta tv',
      'wow',
      'ard mediathek',
      'zdf mediathek',
      'ard plus',
      'servus tv',
      'orf on',
      'play suisse',
      'opto',
      'rtp play',
      'sapo tv',
      'meo go',
      'nos play',
      'streaming abo',
      'directv',
      'dish network',
    ],
  },
};
